import {
  extractRequestContext,
  setUserProfile,
  trackEvent,
} from '@/helpers/mixpanel.server';
import {getPlunkCredentials} from '@/helpers/plunkClient.server';
import {prismaClient} from '@/helpers/prismaClient.server';
import {resolveLocationFromRequest} from '@/server/getUserLocationFromIp';
import {createServerFn} from '@tanstack/react-start';
import {getRequest} from '@tanstack/react-start/server';
import {z} from 'zod';

const WaitlistInputSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  region: z.string().min(1, 'Please select a region').optional(),
  referredByCode: z.string().length(6).optional(),
  userType: z.enum(['attendee', 'organizer']).default('attendee'),
  source: z.string().optional(),
});

export type WaitlistInput = z.infer<typeof WaitlistInputSchema>;

export type WaitlistResponse =
  | {
      success: true;
      message: string;
      referralCode: string;
      isExisting: boolean;
    }
  | {success: false; error: string};

/**
 * Generates a unique 6-character alphanumeric referral code
 */
function generateReferralCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluded I, O, 0, 1 to avoid confusion
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Generates a unique referral code, retrying if collision occurs
 */
async function generateUniqueReferralCode(
  prisma: Awaited<ReturnType<typeof prismaClient>>
): Promise<string> {
  const maxAttempts = 10;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const code = generateReferralCode();
    const existing = await prisma.waitlist.findUnique({
      where: {referralCode: code},
    });

    if (!existing) {
      return code;
    }
  }

  throw new Error('Failed to generate unique referral code');
}

const PLUNK_WAITLIST_SIGNUP_EVENT = 'waitlist.signup';

/**
 * Tracks a waitlist signup event in Plunk to trigger the welcome automation
 */
async function trackWaitlistSignup(
  email: string,
  referralCode: string,
  region: string,
  userType: 'attendee' | 'organizer',
  lat: number | null,
  lng: number | null
): Promise<void> {
  const {publicKey} = await getPlunkCredentials();

  const response = await fetch('https://next-api.useplunk.com/v1/track', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${publicKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      event: PLUNK_WAITLIST_SIGNUP_EVENT,
      email,
      subscribed: true,
      data: {
        referralCode,
        region,
        userType,
        lat,
        lng,
      },
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Failed to track Plunk event:', errorText);
    throw new Error(`Failed to track waitlist signup: ${response.status}`);
  }
}

export const submitWaitlist = createServerFn({method: 'POST'})
  .inputValidator((data: unknown): WaitlistInput => {
    const result = WaitlistInputSchema.safeParse(data);

    if (!result.success) {
      const firstError = result.error.errors[0];
      throw new Error(firstError?.message || 'Invalid input');
    }

    return result.data;
  })
  .handler(async ({data}): Promise<WaitlistResponse> => {
    const prisma = await prismaClient();

    // Resolved once and reused for both geo-lookup and Mixpanel context.
    const request = getRequest();

    // Resolve location from the request IP; falls back to null/unknown for
    // private IPs (local dev) or API errors so signup never fails.
    const {lat, lng, region: geoRegion} = request
      ? await resolveLocationFromRequest(request)
      : {lat: null, lng: null, region: 'unknown'};

    // Prefer the explicitly submitted region (e.g. from the main waitlist form)
    // over the IP-derived one, falling back to IP-derived, then 'unknown'.
    const region = data.region ?? (geoRegion !== 'unknown' ? geoRegion : 'unknown');

    // Check if email already exists
    const existingEntry = await prisma.waitlist.findUnique({
      where: {email: data.email},
    });

    if (existingEntry) {
      return {
        success: true,
        message: "You're already on the waitlist! We'll be in touch soon.",
        referralCode: existingEntry.referralCode,
        isExisting: true,
      };
    }

    // Look up referrer if referral code provided
    let referredById: string | undefined;
    if (data.referredByCode) {
      const referrer = await prisma.waitlist.findUnique({
        where: {referralCode: data.referredByCode},
      });

      if (referrer) {
        referredById = referrer.id;
      }
    }

    try {
      const referralCode = await generateUniqueReferralCode(prisma);

      const newEntry = await prisma.waitlist.create({
        data: {
          email: data.email,
          region,
          lat,
          lng,
          referralCode,
          referredById,
          source: data.source,
        },
      });

      // Track signup event in Plunk to trigger welcome automation
      try {
        await trackWaitlistSignup(
          data.email,
          newEntry.referralCode,
          region,
          data.userType,
          lat,
          lng
        );
      } catch (plunkError) {
        // Log but don't fail the waitlist signup if Plunk tracking fails
        console.error('Failed to track waitlist signup in Plunk:', plunkError);
      }

      // Track in Mixpanel (server-side for reliability)
      try {
        const context = request ? extractRequestContext(request) : undefined;

        // Use the database record ID as the Mixpanel distinct_id
        const distinctId = newEntry.id;

        // Track the signup event
        await trackEvent(
          distinctId,
          'Waitlist Signup',
          {
            region,
            user_type: data.userType,
            referral_code: newEntry.referralCode,
            was_referred: !!referredById,
            source: data.source,
          },
          context
        );

        // Set user profile properties
        await setUserProfile(
          distinctId,
          {
            $email: data.email,
            region,
            user_type: data.userType,
            referral_code: newEntry.referralCode,
            $created: new Date().toISOString(),
          },
          context
        );
      } catch (mixpanelError) {
        // Log but don't fail the waitlist signup if Mixpanel tracking fails
        console.error(
          'Failed to track waitlist signup in Mixpanel:',
          mixpanelError
        );
      }

      return {
        success: true,
        message: "You've been added to the waitlist!",
        referralCode: newEntry.referralCode,
        isExisting: false,
      };
    } catch (error) {
      console.error('Error submitting to waitlist:', error);

      return {
        success: false,
        error: 'Something went wrong. Please try again later.',
      };
    }
  });
